export type TrustArcOptions = {
  /** CSP nonce to attach to injected scripts/styles. */
  nonce?: string;
  /** Cookie policy link surfaced in the notice. */
  cookieLink?: string;
  /** Notice type. 'bb' = bottom blackbar (matches the GSMA config). */
  noticeType?: string;
  /** Called once the consent notice script finishes loading. */
  onReady?: () => void;
};

declare global {
  interface Window {
    truste?: {
      eu?: {
        clickListener?: () => void;
      };
    };
    /** Guards the one-time `message` listener used to reload on submit. */
    __trustArcMessageBound?: boolean;
  }
}

const ASSET_HOST = 'https://consent.trustarc.com';
const DEFAULT_COOKIE_LINK = 'https://www.gsma.com/about-us/legal/cookie-policy';
// GSMA-recommended referrer policy for TrustArc scripts. Set explicitly on
// each script as a fallback in case the server-level `Referrer-Policy`
// header isn't applied in a given environment.
const TRUSTARC_REFERRER_POLICY: ReferrerPolicy = 'origin-when-cross-origin';

/**
 * Inject the TrustArc consent manager programmatically (GTM-style), mirroring
 * the GSMA blackbar snippet:
 *  1) autoblock core, 2) autoblock opt-out (both ordered/blocking),
 *  3) the async consent notice. Also mounts `#consent_blackbar`, binds the
 *  preference-manager `submit_preferences` → reload listener, and applies the
 *  notice button styles.
 *
 * @param domain TrustArc domain id (e.g. `obcbbj`)
 * @param opts   Options (nonce, cookieLink, noticeType, onReady)
 */
export function injectTrustArc(
  domain: string,
  opts: TrustArcOptions = {},
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return; // SSR no-op
  }

  if (!domain || typeof domain !== 'string') {
    throw new Error('injectTrustArc: domain (TrustArc domain id) is required.');
  }

  const {
    nonce,
    cookieLink = DEFAULT_COOKIE_LINK,
    noticeType = 'bb',
    onReady,
  } = opts;

  const CORE_ID = 'trustarc-autoblock-core';
  const OPTOUT_ID = 'trustarc-autoblock-optout';
  const NOTICE_ID = 'trustarc-notice';
  const STYLE_ID = 'trustarc-consent-styles';

  // Already injected? Signal readiness and bail (idempotent). Keyed on the
  // core autoblock script: TrustArc's loader renames the notice tag (to a
  // random `truste_*` id) once it executes, so NOTICE_ID is not a reliable
  // guard and would let consent-state re-renders re-inject the whole stack.
  if (document.getElementById(CORE_ID)) {
    if (onReady) {
      setTimeout(onReady, 0);
    }
    return;
  }

  const head = document.head;
  const firstScript = head.querySelector('script');

  const addScript = (
    id: string,
    src: string,
    { async = false, crossOrigin = false } = {},
  ): HTMLScriptElement => {
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    // Dynamically inserted scripts are force-async; clearing it preserves the
    // autoblock execution order, while the notice stays explicitly async.
    s.async = async;
    s.referrerPolicy = TRUSTARC_REFERRER_POLICY;
    if (crossOrigin) {
      s.crossOrigin = 'anonymous';
    }
    if (nonce) {
      s.setAttribute('nonce', nonce);
    }
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(s, firstScript);
    } else {
      head.appendChild(s);
    }
    return s;
  };

  // 1) Autoblock core (must run before trackers).
  addScript(
    CORE_ID,
    `${ASSET_HOST}/autoblockasset/core.min.js?domain=${domain}`,
  );

  // 2) Autoblock opt-out.
  addScript(OPTOUT_ID, `${ASSET_HOST}/autoblockoptout?domain=${domain}`);

  // 3) Consent notice (blackbar). Async + crossorigin, like the source snippet.
  // `cookieLink` is intentionally left unencoded to match TrustArc's expected
  // query format.
  const noticeSrc =
    `${ASSET_HOST}/notice?domain=${domain}` +
    `&c=teconsent&js=nj&noticeType=${noticeType}&text=true&gtm=1&pcookie` +
    `&cookieLink=${cookieLink}`;
  const notice = addScript(NOTICE_ID, noticeSrc, {
    async: true,
    crossOrigin: true,
  });
  if (onReady) {
    notice.addEventListener('load', () => onReady(), { once: true });
  }

  // Blackbar mount point — TrustArc renders the banner into this element.
  if (!document.getElementById('consent_blackbar')) {
    const blackbar = document.createElement('div');
    blackbar.id = 'consent_blackbar';
    document.body.appendChild(blackbar);
  }

  // Reload once the user submits preferences so consent-gated scripts re-read
  // the updated consent state.
  if (!window.__trustArcMessageBound) {
    window.__trustArcMessageBound = true;
    window.addEventListener(
      'message',
      (event: MessageEvent) => {
        let data: { source?: string; message?: string } | null = null;
        try {
          data = JSON.parse(event.data);
        } catch {
          return; // Non-JSON message — not from the preference manager.
        }
        if (
          data?.source === 'preference_manager' &&
          data.message === 'submit_preferences'
        ) {
          setTimeout(() => window.location.reload(), 300);
        }
      },
      false,
    );
  }

  // Notice button styling carried over from the GSMA snippet.
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    if (nonce) {
      style.setAttribute('nonce', nonce);
    }
    style.textContent = [
      '#truste-show-consent, #truste-consent-button, #truste-consent-required {',
      '  background: #fff;',
      '  border: 1px solid #fff;',
      '  color: #000;',
      '}',
      '#truste-consent-button, #truste-consent-required {',
      '  background: #fff;',
      '  border: 1px solid #fff;',
      '  color: #000;',
      '}',
    ].join('\n');
    head.appendChild(style);
  }
}
